import { useSelector } from 'react-redux';

import { useDocumentDetailsLoading } from '../../../../hooks';
import {
  INSURANCE_CARD_DOCUMENT_SUB_TYPES,
  INSURANCE_CARD_SIDES,
} from '../constants';
import { getInsuranceCardDocument } from './utils';
import useUploadInsuranceCardDocument from './useUploadInsuranceCardDocument';

import type { RootState } from '../../../../../../../store';
import type { InsuranceType } from '../../../../../../../utils/types';

export default function useInsuranceCardDocumentUpload(insuranceType: InsuranceType) {
  const documents = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.onboardingQuestionnaireDetails?.documents,
  );
  const uploadDocumentLoading = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.uploadDocumentLoading,
  );
  const deleteDocumentLoading = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.deleteDocumentLoading,
  );
  const documentDetailsLoading = useDocumentDetailsLoading();
  const uploadDocument = useUploadInsuranceCardDocument();

  const documentSubTypes = INSURANCE_CARD_DOCUMENT_SUB_TYPES[insuranceType];
  const frontSubType = documentSubTypes[INSURANCE_CARD_SIDES.FRONT];
  const backSubType = documentSubTypes[INSURANCE_CARD_SIDES.BACK];

  const frontDocument = getInsuranceCardDocument(documents, frontSubType);
  const backDocument = getInsuranceCardDocument(documents, backSubType);

  const onUploadFront = (file: File, title: string) => uploadDocument(file, title, frontSubType);
  const onUploadBack = (file: File, title: string) => uploadDocument(file, title, backSubType);

  const frontUploadButtonLabel = frontDocument ? 'Replace (Front)' : 'Upload (Front)';
  const backUploadButtonLabel = backDocument ? 'Replace (Back)' : 'Upload (Back)';

  const frontPreviewLoading =
    documentDetailsLoading && Boolean(frontDocument) && !frontDocument?.document_url;
  const backPreviewLoading =
    documentDetailsLoading && Boolean(backDocument) && !backDocument?.document_url;

  return {
    backFileName: backDocument?.title ?? '',
    backFileType: backDocument?.file_type ?? '',
    backPreviewLoading,
    backPreviewUrl: backDocument?.document_url ?? '',
    backUploadButtonLabel,
    deleteDocumentLoading,
    frontFileName: frontDocument?.title ?? '',
    frontFileType: frontDocument?.file_type ?? '',
    frontPreviewLoading,
    frontPreviewUrl: frontDocument?.document_url ?? '',
    frontUploadButtonLabel,
    onUploadBack,
    onUploadFront,
    uploadDocumentLoading,
  };
}
